import React from "react";
import ReactMarkdown from "react-markdown";
import uiStyles from "../../reportViewers/ReportUIPrimitives.module.css";
import styles from "./FunnelAssessmentViewerV2.module.css";

const escapeRegExp = (value = "") =>
  String(value).replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");

const normalizeTitleForMatch = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/^\s*\d+(?:\.\d+)?\.\s*/, "")
    .replace(/\s+assessment\s*$/i, "")
    .replace(/[^\w\s&]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeAssessmentMarkdown = (markdown = "", title = "") => {
  let text = String(markdown || "").replace(/\r/g, "").trim();
  if (!text) return "";

  if (title) {
    const titlePattern = new RegExp(
      `^\\s*\\*\\*\\s*${escapeRegExp(title)}\\s*\\*\\*\\s*\\n?`,
      "i"
    );
    text = text.replace(titlePattern, "");

    const assessmentPattern = new RegExp(
      `^\\s*\\*\\*\\s*${escapeRegExp(title)}\\s+Assessment\\s*\\*\\*\\s*\\n?`,
      "i"
    );
    text = text.replace(assessmentPattern, "");
  }

  // Also remove duplicated leading bold headings with numeric prefixes,
  // e.g. "**2. Fixed Cost Viability Diagnostic**".
  const titleNorm = normalizeTitleForMatch(title);
  const lines = text.split("\n");
  let idx = 0;
  while (idx < lines.length) {
    const line = lines[idx].trim();
    if (!/^\*\*.*\*\*$/.test(line)) break;
    const raw = line.replace(/^\*\*|\*\*$/g, "").trim();
    const rawNorm = normalizeTitleForMatch(raw);
    if (!rawNorm) {
      idx += 1;
      continue;
    }
    if (
      rawNorm === titleNorm ||
      rawNorm === `${titleNorm} assessment` ||
      rawNorm.includes(titleNorm)
    ) {
      idx += 1;
      continue;
    }
    break;
  }
  text = lines.slice(idx).join("\n").trim();

  text = text.replace(/^\s*Summary:\s*/i, "").trim();

  // If present, show only the diagnostic interpretation body.
  // Remove anything before "What Does This Mean?" even if it appears inline.
  const normalizedForSearch = text.replace(/\r/g, "");
  const headingRegex = /(?:#+\s*)?What Does This Mean\?/i;
  const headingMatch = headingRegex.exec(normalizedForSearch);
  if (headingMatch && typeof headingMatch.index === "number") {
    const afterHeading = normalizedForSearch
      .slice(headingMatch.index + headingMatch[0].length)
      .trim();
    if (afterHeading) {
      return afterHeading
        .replace(/\s*[•]\s*/g, "\n- ")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    }
  }

  return text
    .replace(/\s*[•]\s*/g, "\n- ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const FunnelAssessmentViewerV2Base = ({
  markdown,
  title,
  subtitle = "Assessment insight from Funnel Stage diagnostics",
  // badge = "Assessment",
  Icon,
}) => {
  if (!markdown) return null;

  const normalizedMarkdown = normalizeAssessmentMarkdown(markdown, title);
  if (!normalizedMarkdown) return null;

  return (
    <div className={styles.card}>
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroIcon} aria-hidden="true">
            {Icon ? <Icon className={styles.heroIconGlyph} /> : null}
          </div>
          <div>
            {/* <div className={styles.heroBadge}>{badge}</div> */}
            <h4 className={styles.heroTitle}>{title}</h4>
          </div>
        </div>
        <p className={styles.heroSubtitle}>{subtitle}</p>
      </div>

      <div className={styles.sectionCard}>
        {/* <div className={styles.sectionHeader}>{title} Assessment</div> */}
        <div className={styles.markdownBody}>
          <ReactMarkdown
            components={{
              h2: ({ children }) => <h4 className={styles.heading}>{children}</h4>,
              h3: ({ children }) => <h4 className={styles.heading}>{children}</h4>,
              p: ({ children }) => <p className={styles.paragraph}>{children}</p>,
              ul: ({ children }) => <ul className={styles.list}>{children}</ul>,
              ol: ({ children }) => <ol className={styles.list}>{children}</ol>,
              li: ({ children }) => (
                <li className={styles.listItem}>
                  <span className={uiStyles.summaryCheck} aria-hidden="true" />
                  <span className={styles.listItemText}>{children}</span>
                </li>
              ),
              strong: ({ children }) => (
                <strong style={{ fontWeight: 800 }}>{children}</strong>
              ),
            }}
          >
            {normalizedMarkdown}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default FunnelAssessmentViewerV2Base;
